import React, { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useBranding } from "../../context/BrandingContext";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import portalApi from "../../api/portalClient";
import PortalAIChat from "./PortalAIChat";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** When set, the nav item is only shown if the predicate returns true. */
  visibleIf?: "can_view_invoices";
  /** Only shown to portal administrators. A "user" role sees just their tickets. */
  adminOnly?: boolean;
}

const nav: NavItem[] = [
  {
    path: "/portal",
    label: "Dashboard",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  },
  {
    path: "/portal/tickets",
    label: "My Tickets",
    icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
  },
  {
    path: "/portal/devices",
    label: "Devices",
    icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2",
    adminOnly: true,
  },
  {
    path: "/portal/invoices",
    label: "Invoices",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    visibleIf: "can_view_invoices",
    adminOnly: true,
  },
  {
    path: "/portal/reports",
    label: "Reports",
    icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    adminOnly: true,
  },
  {
    path: "/portal/runbooks",
    label: "Runbooks",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
    adminOnly: true,
  },
  {
    path: "/portal/audit",
    label: "Activity",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    adminOnly: true,
  },
  {
    path: "/portal/manage",
    label: "Manage organization",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    adminOnly: true,
  },
];

// Portal paths only an administrator may reach; a "user" is bounced to tickets.
const ADMIN_PATHS = [
  "/portal/devices",
  "/portal/invoices",
  "/portal/reports",
  "/portal/runbooks",
  "/portal/audit",
  "/portal/manage",
];

export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, loading } = usePortalAuth();
  const branding = useBranding();
  const location = useLocation();
  const [showPwModal, setShowPwModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const forcedChange = !!user?.must_change_password;
  useEffect(() => {
    if (forcedChange) setShowPwModal(true);
  }, [forcedChange]);

  // Close the drawer on navigation. Without this, tapping a nav item leaves
  // the overlay covering the page it just opened.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>
    );
  if (!user) return <Navigate to="/portal/login" replace />;

  // Role gate: a "user" can only view/manage their tickets. If one lands on an
  // admin-only portal page (e.g. via an old link), bounce them to tickets.
  const isPortalAdmin = user.role === "administrator";
  if (
    !isPortalAdmin &&
    ADMIN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
  ) {
    return <Navigate to="/portal/tickets" replace />;
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Mobile-only backdrop for the slide-out drawer. Tapping it closes the
        drawer; the transition covers pointer-events too, so a hidden backdrop
        cannot swallow taps meant for the page. */}
      <div
        className={`fixed inset-0 bg-black/40 z-30 md:hidden transition-opacity ${
          mobileNavOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar.
          - md+: a static column, exactly as before
          - <md: a fixed drawer that slides in from the left

        IT USED TO BE `w-64` AT EVERY WIDTH. On a phone that is 256px of a
        ~400px screen, so the portal rendered with about 145px for content:
        the "Submit a Ticket" button sat off the right edge, the tickets table
        wrapped one word per line, and figures like "$0.00" were cut in half.
        The admin console solved this months ago; the portal never got it,
        which matters more here because a CUSTOMER is far likelier to be on a
        phone than a technician is. Same markup as components/Layout so the
        two shells cannot drift apart again. */}
      <div
        /* NOTE THE ABSENT `relative`. It was in the original class list and
           it silently defeated this whole fix: `relative` and `fixed` are
           both position utilities of equal specificity, so the one emitted
           later in Tailwind's stylesheet wins - and that was `relative`. The
           drawer therefore slid off-screen while STILL occupying its 256px
           of flow, leaving the main column 146px of a 402px phone. The
           screenshot looked plausible; only measuring the computed position
           showed `relative` instead of `fixed`. Nothing inside the sidebar
           was absolutely positioned, so it was anchoring nothing. */
        className={`bg-slate-900 text-white flex flex-col shrink-0 z-40 transition-transform
        fixed inset-y-0 left-0 w-64 transform ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        }
        md:static md:translate-x-0`}
      >
        {/* Brand-color top stripe — matches the email template */}
        <div className="h-1 w-full bg-brand" />

        {/* Header with MSP logo */}
        <div className="px-5 py-5 border-b border-slate-800">
          {branding.has_logo && branding.logo_url && (
            <img
              src={branding.logo_url}
              alt={branding.company_name}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              /* No padding: with w-auto the artwork fills the plate exactly, so
                 the white backing is invisible for an opaque logo but still
                 backstops a transparent one, which would otherwise disappear
                 into the dark sidebar. */
              className="h-16 w-auto max-w-[11rem] mb-3 bg-white rounded-lg object-contain"
            />
          )}
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            {branding.portal_eyebrow}
          </div>
          <div className="text-sm font-semibold mt-1 truncate text-white">
            {user.customer_name}
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {nav
            .filter((item) => {
              if (item.adminOnly && !isPortalAdmin) return false;
              if (item.visibleIf === "can_view_invoices") {
                return user.can_view_invoices !== false;
              }
              return true;
            })
            .map((item) => {
              const active =
                location.pathname === item.path ||
                (item.path !== "/portal" && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-brand text-white shadow-sm"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                  {item.label}
                </Link>
              );
            })}
        </nav>

        <div className="px-5 py-4 border-t border-slate-800">
          <div className="text-sm text-slate-200 truncate font-medium">
            {user.full_name || user.email}
          </div>
          <div className="text-xs text-slate-500 truncate mb-3">{user.email}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/portal/security"
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Security
            </Link>
            <span className="text-slate-700">·</span>
            <button
              onClick={() => setShowPwModal(true)}
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Password
            </button>
            <span className="text-slate-700">·</span>
            <button
              onClick={logout}
              className="text-xs text-slate-400 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Phone-only top bar. The drawer has no other way to be opened, and
          it also gives the customer the company name for context now that
          the sidebar is hidden. */}
        <div className="md:hidden flex items-center gap-3 px-3 py-2 bg-slate-900 text-white shrink-0">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-1 hover:bg-slate-800 rounded"
            aria-label="Open menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="text-sm font-semibold truncate flex-1">{user.customer_name}</div>
        </div>
        {/* min-w-0 on the column above: without it a wide child (a table) sets
          the flex item's minimum width and pushes the whole page wider than
          the viewport instead of scrolling inside its own card. */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">{children}</main>
      </div>

      <PortalAIChat />

      {showPwModal && (
        <ChangePasswordModal
          endpoint="/api/v1/portal/change-password"
          apiClient={portalApi}
          forced={forcedChange}
          onClose={() => {
            if (!forcedChange) setShowPwModal(false);
          }}
          onSuccess={() => {
            setShowPwModal(false);
            if (forcedChange) window.location.reload();
          }}
        />
      )}
    </div>
  );
}
